import { FC, memo, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { INPUT_SPECIALISTS } from '../Utils/specialists';
import { ChatInput } from './ChatInput';
import { EmptyState } from './EmptyState';
import { Messages } from './Messages';
import { SpecialistPickerProps } from './SpecialistPicker';
import useChat from './useChat';
import {
  ChatColumn,
  ChatContainer,
  ChatGradient,
  Disclaimer,
  InputSection,
  ScrollArea,
  Scroller
} from './styles';

/** Distância do fim a partir da qual o usuário "rolou para cima" e o auto-scroll pausa. */
const STICK_TO_BOTTOM_THRESHOLD = 80;

export interface ChatProps {
  constructionSiteId: number;
  selectedThreadId?: string;
  setSelectedThreadId: (threadId: string | undefined) => void;
  assistant?: string;
  onViewAll?: () => void;
}

const ChatComponent: FC<ChatProps> = ({
  constructionSiteId,
  selectedThreadId,
  setSelectedThreadId,
  assistant,
  onViewAll
}) => {
  const { t } = useTranslation('activities');
  const scrollerRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const {
    value,
    setValue,
    messages,
    isSending,
    isLoadingThread,
    hasConversation,
    send,
    submit,
    selectedSpecialist,
    selectSpecialist
  } = useChat({ constructionSiteId, selectedThreadId, setSelectedThreadId, assistant });

  const specialistPicker: SpecialistPickerProps = {
    options: INPUT_SPECIALISTS,
    selected: selectedSpecialist,
    onSelect: selectSpecialist,
    onViewAll
  };

  useLayoutEffect(() => {
    stickToBottomRef.current = true;
  }, [selectedThreadId]);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller && stickToBottomRef.current) {
      scroller.scrollTop = scroller.scrollHeight;
    }
  }, [messages]);

  const handleScroll = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    stickToBottomRef.current =
      scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <
      STICK_TO_BOTTOM_THRESHOLD;
  };

  return (
    <ChatContainer>
      <ScrollArea>
        <Scroller ref={scrollerRef} onScroll={handleScroll}>
          <ChatColumn>
            {hasConversation ? (
              <Messages
                messages={messages}
                isLoading={isLoadingThread}
                isSending={isSending}
                specialistPicker={specialistPicker}
                onEditPrompt={send}
              />
            ) : (
              <EmptyState onSelectPrompt={send} />
            )}
          </ChatColumn>
        </Scroller>
        <ChatGradient />
      </ScrollArea>

      <InputSection>
        <ChatInput
          value={value}
          onChange={setValue}
          onSubmit={submit}
          disabled={isSending}
          specialistPicker={specialistPicker}
        />
        <Disclaimer>{t('aiChat.footerDisclaimer')}</Disclaimer>
      </InputSection>
    </ChatContainer>
  );
};

ChatComponent.displayName = 'Chat';

export const Chat = memo(ChatComponent);
